/**
 * The interactive Leadership report (Rusty, 12 Oct): the overview's numbers,
 * recomputed on the server for a date range and the global filters, with
 * every number clickable.
 *
 * - Range: last 7 days, last 30 days, since launch (default) or custom.
 *   Filters: team, channel, incident vs request, and site when the tenant
 *   fills it reliably. All of it lives in the URL, so a view can be shared.
 *   No company filter: the only company-like field, Ivanti's
 *   OrganizationUnitID, is an org-unit stamp, not a company (12 Oct).
 * - Each top tile shows ▲/▼ against the previous equal-length period (open
 *   now and past target: against the data one range-length earlier), coloured
 *   by what is good for that number.
 * - Every number, donut slice, bar segment, team row and age block opens a
 *   drawer with the tickets behind it (sortable, CSV), each linking to the
 *   ticket in the HelpDesk.
 * - ⓘ on every number gives its exact definition; ? on each section asks
 *   Rusty about that section.
 * - "Download Excel" (server-side) and "Download PDF" (the browser's print,
 *   A4 landscape).
 */

import { Download, Icon, Search, X } from "@norican/icons";
import {
  Badge,
  Button,
  Card,
  Chip,
  ChipGroup,
  cn,
  DataGrid,
  type DataGridColumn,
  type DataGridSort,
  DatePicker,
  Drawer,
  FilterSelect,
  Input,
  parseDate,
  Toggle,
  ToggleGroup,
  Typography,
} from "@norican/react";
import { type ReactNode, useId, useMemo, useRef, useState } from "react";

import { EmptyStateBlock, ErrorState, LoadingRows, normaliseApiError } from "@/components/common";
import { useLanguage, useT } from "@/i18n";
import type { InsightsCount, InsightsResponse, InsightsTicket, InsightsTile } from "@/lib/api";

import { AskReportPanel } from "./ask-report-panel";
import {
  distinct,
  filterTickets,
  hasTicketFilter,
  NO_TICKET_FILTER,
  type TicketFilter,
} from "./export-helpers";
import { type Drill, saveBlob, useInsightTickets, useReportInsights } from "./insights-api";
import {
  ChannelBars,
  type DayBar,
  Donut,
  FlowChart,
  Key,
  type Part,
  Ring,
  TeamBars,
} from "./insights-charts";
import { ExportMenu, ExportProvider, type ExportScope } from "./insights-export";
import {
  AGE_COLORS,
  AGE_KEYS,
  C,
  CHANNEL_KEYS,
  channelColor,
  type DefinitionKey,
  deltaView,
  formatValue,
  hasFilters,
  type InsightsState,
  keyOf,
  type KindFilter,
  MAILBOX_COLORS,
  mailboxName,
  movesLine,
  RANGE_PRESETS,
  type RangePreset,
  round1,
  slaStateKey,
  sortRows,
  STATUS_COLORS,
  STATUS_KEYS,
  TEAM_COLUMNS,
  type TeamColumn,
  ticketHref,
  ticketLabel,
  TILE_KEYS,
  type TileKey,
  toCsv,
} from "./insights-helpers";
import { GettingSection, MovesSection, PeopleSection, SitesSection } from "./insights-more";
import { Definition, LinkNumber, Section, SubTitle } from "./insights-parts";
import type { NoteContext } from "./note-dialog";
import { RefreshLine } from "./refresh-line";
import { HELPDESK_OVERVIEW } from "./report-helpers";

const ANY = "ANY";

export interface InsightsViewProps {
  state: InsightsState;
  onChange: (next: InsightsState) => void;
  /** "Ask about this report" (16 Oct): the side panel, opened from the page header. */
  askOpen?: boolean;
  onAskOpenChange?: (open: boolean) => void;
  /** "Add a note" under an answer, with its question and answer. */
  onAddNote?: (context: NoteContext) => void;
}

export function InsightsView({
  state,
  onChange,
  askOpen = false,
  onAskOpenChange,
  onAddNote,
}: InsightsViewProps) {
  const { t } = useT("common");
  const insights = useReportInsights(HELPDESK_OVERVIEW, state);
  const [drill, setDrill] = useState<Drill | null>(null);

  const data = insights.data;
  const open = (measure: Drill["measure"], value: string | null, title: string) =>
    setDrill({ measure, value, title });

  const notFound = insights.error != null && normaliseApiError(insights.error).status === 404;
  const exportScope = useMemo<ExportScope | null>(
    () =>
      data
        ? {
            state,
            first: data.meta.first,
            last: data.meta.last,
            footer: t("leadershipReport.live.export.footer", {
              tenant: data.meta.tenant,
              first: data.meta.first,
              last: data.meta.last,
              filters: data.meta.filters,
            }),
          }
        : null,
    [data, state, t],
  );

  return (
    <div className="flex flex-col gap-4" data-print-page>
      <Filters state={state} onChange={onChange} data={data} />

      <div>
        <RefreshLine
          pulledAt={data?.meta.pulled_at}
          extra={[
            data ? t("leadershipReport.live.tenant", { tenant: data.meta.tenant }) : null,
            data?.meta.tests_excluded
              ? t("leadershipReport.live.testsExcluded", { count: data.meta.tests_excluded })
              : null,
          ]}
        />
      </div>

      {insights.isLoading ? (
        <LoadingRows rows={8} />
      ) : notFound ? (
        <EmptyStateBlock
          title={t("leadershipReport.live.noData.title")}
          description={t("leadershipReport.live.noData.hint")}
        />
      ) : insights.error != null ? (
        <ErrorState error={insights.error} onRetry={() => void insights.refetch()} />
      ) : data ? (
        <ExportProvider value={exportScope}>
          <div className={cn("flex flex-col gap-4", insights.isFetching && "opacity-70")}>
            <RangeLine data={data} />
            {/* `lr-<section>`: where "Ask about this report" links to. */}
            <div id="lr-tiles" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <Tiles data={data} open={open} />
            </div>
            <div id="lr-teams" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <TeamsSection data={data} open={open} />
            </div>
            <div id="lr-status" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <StatusSection data={data} open={open} />
            </div>
            <div id="lr-sla" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <SlaSection data={data} open={open} />
            </div>
            <div id="lr-intake" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <IntakeSection data={data} open={open} />
            </div>
            <div id="lr-getting" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <GettingSection data={data} open={open} />
            </div>
            <div id="lr-sites" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <SitesSection data={data} open={open} />
            </div>
            <div id="lr-people" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <PeopleSection data={data} open={open} />
            </div>
            <div id="lr-moves" className="scroll-mt-[calc(var(--app-header-height,4rem)+6.5rem)]">
              <MovesSection data={data} open={open} />
            </div>
          </div>
        </ExportProvider>
      ) : null}

      <TicketDrawer state={state} drill={drill} onClose={() => setDrill(null)} />
      {onAskOpenChange ? (
        <AskReportPanel
          open={askOpen}
          onOpenChange={onAskOpenChange}
          state={state}
          onOpenTickets={open}
          onAddNote={(context) => onAddNote?.(context)}
        />
      ) : null}
    </div>
  );
}

// ── Toolbar ─────────────────────────────────────────────────────────────────

function Filters({
  state,
  onChange,
  data,
}: {
  state: InsightsState;
  onChange: (next: InsightsState) => void;
  data: InsightsResponse | undefined;
}) {
  const { t } = useT("common");
  const options = data?.options;
  const rangeItems = RANGE_PRESETS.map((value) => ({
    value,
    label: t(`leadershipReport.live.range.${value}`),
  }));
  const kindItems = [
    { value: ANY, label: t("leadershipReport.live.viz.kindAll") },
    { value: "incident", label: t("leadershipReport.live.sites.incidents") },
    { value: "request", label: t("leadershipReport.live.sites.requests") },
  ];
  const channelLabel = useChannelLabel();
  const channelsId = useId();
  const today = data?.meta.last ?? new Date().toISOString().slice(0, 10);

  function setRange(range: RangePreset) {
    if (range === "custom") {
      const last = data?.meta.last ?? today;
      const first = data?.meta.first ?? last;
      onChange({ ...state, range, from: state.from ?? first, to: state.to ?? last });
    } else {
      onChange({ ...state, range, from: null, to: null });
    }
  }

  const kindValue = state.kind ?? ANY;
  return (
    <div
      className="border-border bg-bg/95 sticky top-[var(--app-header-height,4rem)] z-10 -mx-1 flex flex-col gap-1.5 border-b px-1 py-2 backdrop-blur"
      data-print-hide
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <ToggleGroup.Root
          appearance="segmented"
          size="sm"
          value={[state.range]}
          onValueChange={(next) => {
            const [value] = next;
            if (value && value !== state.range) {
              setRange(value as RangePreset);
            }
          }}
          aria-label={t("leadershipReport.live.range.label")}
        >
          {rangeItems.map((item) => (
            <Toggle key={item.value} value={item.value}>
              {item.label}
            </Toggle>
          ))}
        </ToggleGroup.Root>
        {state.range === "custom" ? (
          <>
            <DatePicker
              size="sm"
              className="w-40"
              aria-label={t("leadershipReport.live.range.from")}
              value={state.from ? parseDate(state.from) : null}
              maxValue={state.to ? parseDate(state.to) : undefined}
              onValueChange={(value) => value && onChange({ ...state, from: value.toString() })}
            />
            <DatePicker
              size="sm"
              className="w-40"
              aria-label={t("leadershipReport.live.range.to")}
              value={state.to ? parseDate(state.to) : null}
              minValue={state.from ? parseDate(state.from) : undefined}
              onValueChange={(value) => value && onChange({ ...state, to: value.toString() })}
            />
          </>
        ) : null}
        <ToggleGroup.Root
          appearance="segmented"
          size="sm"
          value={[kindValue]}
          onValueChange={(next) => {
            const [value] = next;
            if (value && value !== kindValue) {
              onChange({ ...state, kind: value === ANY ? null : (value as KindFilter) });
            }
          }}
          aria-label={t("leadershipReport.live.filter.kind")}
        >
          {kindItems.map((item) => (
            <Toggle key={item.value} value={item.value}>
              {item.label}
            </Toggle>
          ))}
        </ToggleGroup.Root>
        <FilterSelect
          size="sm"
          options={(options?.teams ?? []).map((value) => ({ value, label: value }))}
          value={state.teams}
          onValueChange={(teams) => onChange({ ...state, teams })}
          placeholder={t("leadershipReport.live.filter.team")}
          noun={t("leadershipReport.live.filter.teamNoun")}
          searchPlaceholder={t("leadershipReport.live.filter.search")}
          aria-label={t("leadershipReport.live.filter.team")}
        />
        {data?.meta.site_available ? (
          <FilterSelect
            size="sm"
            options={(options?.sites ?? []).map((value) => ({ value, label: value }))}
            value={state.sites}
            onValueChange={(sites) => onChange({ ...state, sites })}
            placeholder={t("leadershipReport.live.filter.site")}
            noun={t("leadershipReport.live.filter.siteNoun")}
            searchPlaceholder={t("leadershipReport.live.filter.search")}
            aria-label={t("leadershipReport.live.filter.site")}
          />
        ) : null}
        {hasFilters(state) ? (
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            startIcon={<Icon icon={X} size="sm" />}
            onClick={() => onChange({ ...state, teams: [], channels: [], kind: null, sites: [] })}
          >
            {t("leadershipReport.live.filter.clear")}
          </Button>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span id={channelsId} className="text-fg-muted text-[12.5px] font-semibold">
          {t("leadershipReport.live.filter.channel")}
        </span>
        <ChipGroup.Root
          multiple
          aria-labelledby={channelsId}
          value={state.channels}
          onValueChange={(channels) => onChange({ ...state, channels: [...channels].sort() })}
        >
          {(options?.channels ?? []).map((value) => (
            <Chip
              key={value}
              value={value}
              size="sm"
              startIcon={
                <i
                  className="inline-block h-2.5 w-2.5 rounded-[3px]"
                  style={{ background: channelColor(value) }}
                  aria-hidden
                />
              }
            >
              {channelLabel(value)}
            </Chip>
          ))}
        </ChipGroup.Root>
        {[...state.teams, ...state.sites].map((value) => (
          <Chip
            key={`picked-${value}`}
            size="sm"
            tone="accent"
            onRemove={() =>
              onChange({
                ...state,
                teams: state.teams.filter((v) => v !== value),
                sites: state.sites.filter((v) => v !== value),
              })
            }
            removeLabel={`${t("leadershipReport.live.filter.clear")}: ${value}`}
          >
            {value}
          </Chip>
        ))}
        <span className="text-fg-muted ms-auto text-[12px]">
          {t("leadershipReport.live.viz.filtersApply")}
        </span>
      </div>
    </div>
  );
}

// ── Labels ──────────────────────────────────────────────────────────────────

function useChannelLabel() {
  const { t } = useT("common");
  return (group: string): string => {
    const key = keyOf(CHANNEL_KEYS, group);
    return key ? t(`leadershipReport.live.channel.${key}`) : group;
  };
}

function useLabels() {
  const { t } = useT("common");
  const channel = useChannelLabel();
  return {
    status: (g: string): string => {
      const key = keyOf(STATUS_KEYS, g);
      return key ? t(`leadershipReport.live.statusGroup.${key}`) : g;
    },
    age: (a: string): string => {
      const key = keyOf(AGE_KEYS, a);
      return key ? t(`leadershipReport.live.age.${key}`) : a;
    },
    channel,
    mailbox: (m: string) => mailboxName(m) || t("leadershipReport.live.channel.otherEmail"),
    kind: (k: string) =>
      k === "Incident"
        ? t("leadershipReport.live.kind.incident")
        : t("leadershipReport.live.kind.request"),
    sla: (s: string): string => t(`leadershipReport.live.sla.state.${slaStateKey(s)}`),
  };
}

// ── Building blocks ─────────────────────────────────────────────────────────

export type Open = (measure: Drill["measure"], value: string | null, title: string) => void;

function Delta({ tile }: { tile: InsightsTile }) {
  const { t } = useT("common");
  const view = deltaView(tile, t("leadershipReport.live.delta.pts"));
  if (!view) {
    return (
      <Typography variant="caption" tone="muted" className="mt-1 block">
        {t("leadershipReport.live.delta.none")}
      </Typography>
    );
  }
  const previous = formatValue(tile.previous, tile.unit);
  const basis =
    tile.compared_with === "snapshot"
      ? t("leadershipReport.live.delta.vsSnapshot", { value: previous })
      : t("leadershipReport.live.delta.vsPeriod", { value: previous });
  const color =
    view.tone === "good"
      ? "text-[#2f9e44]"
      : view.tone === "bad"
        ? "text-[#e03131]"
        : "text-fg-muted";
  return (
    <div className="mt-1 text-[12.5px]">
      <span className={cn("font-bold", color)}>
        {view.arrow} {view.text}
      </span>{" "}
      <span className="text-fg-muted">{basis}</span>
    </div>
  );
}

function Tile({
  color,
  title,
  definition,
  tile,
  children,
}: {
  color: string;
  title: string;
  definition: string;
  tile: InsightsTile;
  children: ReactNode;
}) {
  return (
    <div
      className="border-border bg-bg break-inside-avoid rounded-2xl border border-t-[6px] px-4 pt-3.5 pb-4 text-center"
      style={{ borderTopColor: color }}
    >
      <div className="mb-1 flex items-center justify-center gap-1">
        <span className="text-[15px] font-bold" style={{ color }}>
          {title}
        </span>
        <Definition text={definition} label={title} />
      </div>
      {children}
      <Delta tile={tile} />
    </div>
  );
}

function RangeLine({ data }: { data: InsightsResponse }) {
  const { t } = useT("common");
  const m = data.meta;
  return (
    <Typography variant="body-sm" tone="muted">
      {t("leadershipReport.live.rangeLine", { first: m.first, last: m.last, days: m.days })}
      {m.previous_first && m.previous_last
        ? ` · ${t("leadershipReport.live.comparedWith", { first: m.previous_first, last: m.previous_last })}`
        : ` · ${t("leadershipReport.live.noComparison")}`}
      {m.filters !== "none"
        ? ` · ${t("leadershipReport.live.filtersLine", { filters: m.filters })}`
        : ""}
    </Typography>
  );
}

const tileOf = (data: InsightsResponse, key: string) =>
  [...data.tiles, ...(data.sla.tiles ?? [])].find((tile) => tile.key === key);

const partOf = (tile: InsightsTile | undefined, key: string): InsightsCount | undefined =>
  tile?.parts?.find((p) => p.key === key);

/** A tile key as a label key ("raised" when unknown). */
function tileKey(key: string): TileKey {
  return (TILE_KEYS as readonly string[]).includes(key) ? (key as TileKey) : "raised";
}

// ── Sections ────────────────────────────────────────────────────────────────

function Tiles({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const L = useLabels();
  const ref = useRef<HTMLDivElement>(null);
  const raised = tileOf(data, "raised");
  const done = tileOf(data, "done");
  const openNow = tileOf(data, "open");
  const late = tileOf(data, "past_target");
  const rft = tileOf(data, "rft");
  const share = tileOf(data, "share");
  if (!raised || !done || !openNow || !late || !rft || !share) {
    return null;
  }
  const T = (key: string) => t(`leadershipReport.live.tile.${tileKey(key)}`);
  const D = (key: DefinitionKey) => t(`leadershipReport.live.def.${key}`);
  const count = (tile: InsightsTile, key: string) => partOf(tile, key)?.count ?? 0;
  const part = (tile: InsightsTile, key: string, label: string, color: string): Part => {
    const p = partOf(tile, key);
    return {
      id: key,
      label,
      value: p?.count ?? 0,
      color,
      onSelect: p ? () => open(p.measure, p.value ?? null, `${T(tile.key)}: ${label}`) : undefined,
    };
  };
  const inc = part(raised, "Incident", L.kind("Incident"), C.incident);
  const req = part(raised, "Service Request", L.kind("Service Request"), C.request);
  const raisedN = raised.count ?? 0;
  const doneParts = [
    part(done, "done", t("leadershipReport.live.part.done"), C.done),
    part(done, "cancelled", t("leadershipReport.live.part.cancelled"), C.migrated),
  ];
  const openParts = [
    part(openNow, "period", t("leadershipReport.live.part.inPeriod"), C.open),
    part(openNow, "migrated", t("leadershipReport.live.part.migrated"), C.migrated),
    part(openNow, "other", t("leadershipReport.live.part.outside"), C.before),
  ];
  const rftParts = [part(rft, "moved", t("leadershipReport.live.part.moved"), C.late)];

  return (
    <div ref={ref}>
      <div className="mb-1.5 flex justify-end" data-print-hide>
        <ExportMenu title={t("leadershipReport.live.export.tiles")} target={ref} section="tiles" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 print:grid-cols-5">
        <Tile color={C.request} title={T("raised")} definition={D("raised")} tile={raised}>
          <Donut
            parts={[inc, req]}
            big={raisedN}
            small={t("leadershipReport.live.unit.tickets")}
            label={T("raised")}
          />
          <Key parts={[inc, req]} total={raisedN} />
          <button
            type="button"
            className="text-fg-muted mt-1.5 text-[12px] hover:underline"
            onClick={() => open("migrated", null, t("leadershipReport.live.part.migrated"))}
          >
            {t("leadershipReport.live.exclMigrated", { count: count(raised, "migrated") })}
          </button>
        </Tile>
        <Tile color={C.done} title={T("done")} definition={D("done")} tile={done}>
          <Ring
            pct={done.value}
            color={C.done}
            big={done.count ?? 0}
            small={t("leadershipReport.live.ofRaised", { pct: formatValue(done.value, "pct") })}
            label={T("done")}
            onSelect={() => open("done", null, T("done"))}
          />
          <Key parts={doneParts} total={raisedN} />
        </Tile>
        <Tile color={C.open} title={T("open")} definition={D("open")} tile={openNow}>
          <Donut
            parts={openParts}
            big={openNow.count ?? 0}
            small={t("leadershipReport.live.unit.open")}
            label={T("open")}
          />
          <Key parts={openParts} />
          <button
            type="button"
            onClick={() => open("past_target", null, T("past_target"))}
            className="mt-2 inline-block rounded-full border border-[#ffc9c9] bg-[#fff5f5] px-3 py-0.5 text-[13.5px] font-bold text-[#e03131]"
          >
            {t("leadershipReport.live.pastTargetPill", { count: late.count ?? 0 })}
          </button>
          <Definition text={D("past_target")} label={T("past_target")} />
          <Delta tile={late} />
        </Tile>
        <Tile color={C.rft} title={T("rft")} definition={D("rft")} tile={rft}>
          <Ring
            pct={rft.value}
            color={C.rft}
            big={formatValue(rft.value, "pct")}
            small={t("leadershipReport.live.goal", { goal: rft.goal ?? 85 })}
            goal={rft.goal}
            label={T("rft")}
            onSelect={() => open("rft", null, T("rft"))}
          />
          <Key parts={rftParts} />
          <div className="text-fg-muted mt-1.5 text-[12px]">
            {t("leadershipReport.live.ofChecked", { count: rft.of ?? 0 })}
          </div>
        </Tile>
        <Tile
          color={channelColor("IT HelpDesk")}
          title={T("share")}
          definition={D("share")}
          tile={share}
        >
          <Ring
            pct={share.value}
            color={channelColor("IT HelpDesk")}
            big={formatValue(share.value, "pct")}
            small={t("leadershipReport.live.ofRaisedCount", {
              count: share.count ?? 0,
              of: share.of ?? 0,
            })}
            label={T("share")}
            onSelect={() => open("app", null, T("share"))}
          />
        </Tile>
      </div>
    </div>
  );
}

function TeamsSection({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const columnTitle = (column: string) =>
    t(
      `leadershipReport.live.teamColumn.${(TEAM_COLUMNS as readonly string[]).includes(column) ? (column as TeamColumn) : "open"}`,
    );
  return (
    <Section
      title={t("leadershipReport.live.teams.title")}
      definition={t("leadershipReport.live.def.teams")}
      exportAs="teams"
    >
      <div className="text-fg-muted mb-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px]">
        <span className="inline-flex items-center gap-1.5">
          <i
            className="inline-block h-[11px] w-[11px] rounded-[3px]"
            style={{ background: C.open }}
          />
          {t("leadershipReport.live.teams.openNow")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i
            className="inline-block h-[11px] w-[11px] rounded-[3px]"
            style={{ background: C.late }}
          />
          {t("leadershipReport.live.teams.pastTarget")}
        </span>
        <span>⏸ {t("leadershipReport.live.teamColumn.waiting")}</span>
        <span>⌛ {t("leadershipReport.live.teamColumn.older")}</span>
        <span>⇄ {t("leadershipReport.live.teams.moves")}</span>
      </div>
      {data.teams.length === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {t("leadershipReport.live.nothing")}
        </Typography>
      ) : (
        <TeamBars
          rows={data.teams.map((r) => ({
            team: r.team,
            open: r.open,
            late: r.late,
            waiting: r.waiting,
            older: r.older,
            movesIn: r.moves_in,
            movesOut: r.moves_out,
          }))}
          labels={{
            pastTarget: (n) => t("leadershipReport.live.pastTargetShort", { count: n }),
            waiting: columnTitle("waiting"),
            older: columnTitle("older"),
            moves: t("leadershipReport.live.teams.moves"),
          }}
          onSelect={(team, column) =>
            open("team", `${team}|${column}`, `${team}: ${columnTitle(column)}`)
          }
        />
      )}
    </Section>
  );
}

function StatusSection({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const { locale } = useLanguage();
  const L = useLabels();
  const raised = tileOf(data, "raised")?.count ?? 0;
  const statusParts: Part[] = data.status
    .filter((c) => c.count > 0)
    .map((c) => ({
      id: c.key,
      label: L.status(c.key),
      value: c.count,
      color: STATUS_COLORS[c.key] ?? C.migrated,
      onSelect: () => open(c.measure, c.value ?? null, L.status(c.key)),
    }));
  const ageParts: Part[] = data.ages.map((c, i) => ({
    id: c.key,
    label: L.age(c.key),
    value: c.count,
    color: AGE_COLORS[i % AGE_COLORS.length] ?? C.request,
    onSelect: () =>
      open(c.measure, c.value ?? null, `${t("leadershipReport.live.status.age")}: ${L.age(c.key)}`),
  }));
  const openAll = data.ages.reduce((sum, c) => sum + c.count, 0);
  const old = data.ages.find((c) => c.key === "> 7 days")?.count ?? 0;
  return (
    <Section
      title={t("leadershipReport.live.status.title")}
      definition={t("leadershipReport.live.def.status")}
      exportAs="status"
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1fr_1.2fr] print:grid-cols-[1fr_1fr_1.2fr]">
        <div>
          <SubTitle
            text={t("leadershipReport.live.status.byStatus")}
            definition={t("leadershipReport.live.def.status")}
          />
          <Donut
            parts={statusParts}
            big={raised}
            small={t("leadershipReport.live.unit.raised")}
            label={t("leadershipReport.live.status.byStatus")}
          />
          <Key parts={statusParts} total={raised} />
        </div>
        <div>
          <SubTitle
            text={t("leadershipReport.live.status.age")}
            definition={t("leadershipReport.live.def.age")}
          />
          <AgeBlocks parts={ageParts} />
          <div className="text-fg-muted mt-1.5 text-[12px]">
            {t("leadershipReport.live.status.ageNote", { count: openAll, old })}
          </div>
        </div>
        <div>
          <SubTitle
            text={t("leadershipReport.live.status.flow")}
            definition={t("leadershipReport.live.def.flow")}
          />
          <FlowChart
            days={data.flow}
            locale={locale}
            labels={{
              raised: t("leadershipReport.live.status.raised"),
              resolved: t("leadershipReport.live.status.resolved"),
            }}
            onSelect={(day, which) =>
              open(
                which === "created" ? "day_created" : "day_resolved",
                day,
                `${day}: ${which === "created" ? t("leadershipReport.live.status.raised") : t("leadershipReport.live.status.resolved")}`,
              )
            }
          />
        </div>
      </div>
    </Section>
  );
}

function AgeBlocks({ parts }: { parts: Part[] }) {
  const shown = parts.filter((p) => p.value > 0);
  return (
    <div className="mt-2.5 flex h-16 gap-[3px]">
      {shown.map((part, index) => (
        <button
          key={part.id}
          type="button"
          onClick={part.onSelect}
          title={`${part.label}: ${part.value}`}
          className={cn(
            "flex min-w-11 cursor-pointer flex-col items-center justify-center rounded-lg hover:opacity-85 focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none",
            index === 0 && part.color === AGE_COLORS[0] ? "text-[#1864ab]" : "text-white",
          )}
          style={{ flex: part.value, background: part.color }}
        >
          <b className="text-xl">{part.value}</b>
          <span className="text-[11px]">{part.label}</span>
        </button>
      ))}
    </div>
  );
}

function SlaSection({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const sla = data.sla;
  const resp = tileOf(data, "sla_response");
  const res = tileOf(data, "sla_resolution");
  const late = tileOf(data, "past_target");
  const openAll = tileOf(data, "open")?.count ?? 0;
  const T = (
    key:
      | "response"
      | "resolution"
      | "responseOnTime"
      | "resolutionOnTime"
      | "onTime"
      | "late"
      | "openPast"
      | "due",
  ) => t(`leadershipReport.live.sla.${key}`);
  const clock = (which: "response" | "resolution", tile: InsightsTile | undefined) => {
    const c = sla[which];
    const measure = which === "response" ? "sla_response" : "sla_resolution";
    return (
      <div className="text-center">
        <SubTitle text={T(which)} definition={t("leadershipReport.live.def.sla")} />
        <Ring
          pct={c.pct}
          color={C.done}
          big={formatValue(c.pct, "pct")}
          small={T(which === "response" ? "responseOnTime" : "resolutionOnTime")}
          label={T(which)}
          onSelect={() => open(measure, null, T(which))}
        />
        <div className="text-fg-muted mt-1.5 flex flex-wrap justify-center gap-x-2 text-[12px]">
          <LinkNumber onClick={() => open(measure, "met", `${T(which)}: ${T("onTime")}`)}>
            {t("leadershipReport.live.sla.onTimeN", { count: c.met })}
          </LinkNumber>
          <LinkNumber onClick={() => open(measure, "missed", `${T(which)}: ${T("late")}`)}>
            {t("leadershipReport.live.sla.lateN", { count: c.late })}
          </LinkNumber>
          <LinkNumber onClick={() => open(measure, "breached", `${T(which)}: ${T("openPast")}`)}>
            {t("leadershipReport.live.sla.openPastN", { count: c.open_past })}
          </LinkNumber>
        </div>
        {tile ? <Delta tile={tile} /> : null}
      </div>
    );
  };
  return (
    <Section
      title={t("leadershipReport.live.sla.title")}
      definition={t("leadershipReport.live.def.sla")}
      exportAs="sla"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 print:grid-cols-4">
        {clock("response", resp)}
        {clock("resolution", res)}
        <div className="text-center">
          <SubTitle
            text={t("leadershipReport.live.tile.past_target")}
            definition={t("leadershipReport.live.def.past_target")}
          />
          <button
            type="button"
            className="mt-5 text-[56px] leading-tight font-extrabold text-[#e03131] hover:underline"
            onClick={() => open("past_target", null, t("leadershipReport.live.tile.past_target"))}
          >
            {late?.count ?? 0}
          </button>
          <div className="text-fg-muted text-[12px]">
            {t("leadershipReport.live.sla.pastNote", { count: openAll })}{" "}
            <LinkNumber onClick={() => open("past_target", "response", T("response"))}>
              {t("leadershipReport.live.sla.responseN", { count: sla.open_response_breached })}
            </LinkNumber>{" "}
            ·{" "}
            <LinkNumber onClick={() => open("past_target", "resolution", T("resolution"))}>
              {t("leadershipReport.live.sla.resolutionN", { count: sla.open_resolution_breached })}
            </LinkNumber>
          </div>
          {late ? <Delta tile={late} /> : null}
        </div>
        <div className="text-center">
          <SubTitle text={T("due")} definition={t("leadershipReport.live.def.due")} />
          <button
            type="button"
            className="mt-5 text-[56px] leading-tight font-extrabold text-[#f59f00] hover:underline"
            onClick={() => open("due_24h", null, T("due"))}
          >
            {sla.due_response_24h + sla.due_resolution_24h}
          </button>
          <div className="text-fg-muted text-[12px]">
            <LinkNumber
              onClick={() => open("due_24h", "response", `${T("due")}: ${T("response")}`)}
            >
              {t("leadershipReport.live.sla.responseN", { count: sla.due_response_24h })}
            </LinkNumber>{" "}
            ·{" "}
            <LinkNumber
              onClick={() => open("due_24h", "resolution", `${T("due")}: ${T("resolution")}`)}
            >
              {t("leadershipReport.live.sla.resolutionN", { count: sla.due_resolution_24h })}
            </LinkNumber>
          </div>
        </div>
      </div>
      <div className="text-fg-muted mt-2 text-[12px]">{t("leadershipReport.live.def.sla")}</div>
    </Section>
  );
}

function IntakeSection({ data, open }: { data: InsightsResponse; open: Open }) {
  const { t } = useT("common");
  const { locale } = useLanguage();
  const L = useLabels();
  const total = data.channels.reduce((sum, c) => sum + c.count, 0);
  const chParts: Part[] = data.channels.map((c) => ({
    id: c.key,
    label: L.channel(c.key),
    value: c.count,
    color: channelColor(c.key),
    onSelect: () => open(c.measure, c.value ?? null, L.channel(c.key)),
  }));
  const emailTotal = data.mailboxes.reduce((sum, c) => sum + c.count, 0);
  const mbParts: Part[] = data.mailboxes.map((c, i) => ({
    id: c.key,
    label: L.mailbox(c.key),
    value: c.count,
    color: MAILBOX_COLORS[i % MAILBOX_COLORS.length] ?? C.migrated,
    onSelect: () => open(c.measure, c.value ?? null, `${L.channel("Email")}: ${L.mailbox(c.key)}`),
  }));
  const days: DayBar[] = data.daily.map((d) => ({
    date: d.date,
    total: d.total,
    share: d.share,
    parts: (d.parts ?? []).map((p) => ({
      id: p.key,
      label: L.channel(p.key),
      value: p.count,
      color: channelColor(p.key),
      onSelect: () => open(p.measure, p.value ?? null, `${d.date}: ${L.channel(p.key)}`),
    })),
  }));
  const legend = data.channels.map((c) => c.key);
  return (
    <Section
      title={t("leadershipReport.live.intake.title")}
      definition={t("leadershipReport.live.def.channels")}
      exportAs="intake"
    >
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.2fr] print:grid-cols-[1fr_1.2fr]">
        <div>
          <Donut
            parts={chParts}
            big={total}
            small={t("leadershipReport.live.unit.tickets")}
            label={t("leadershipReport.live.intake.title")}
          />
          <Key parts={chParts} total={total} />
        </div>
        <div>
          <SubTitle
            text={t("leadershipReport.live.intake.mailboxes", { count: emailTotal })}
            definition={t("leadershipReport.live.def.mailboxes")}
          />
          <AgeBlocks parts={mbParts.map((p) => ({ ...p }))} />
          <div className="text-fg-muted mt-1.5 text-[12px]">
            {t("leadershipReport.live.def.mailboxes")}
          </div>
        </div>
      </div>
      <div className="mt-4">
        <SubTitle
          text={t("leadershipReport.live.intake.daily")}
          definition={t("leadershipReport.live.def.daily")}
        />
        <ChannelBars
          days={days}
          locale={locale}
          label={t("leadershipReport.live.intake.daily")}
          onSelectDay={(day) =>
            open("day_created", day, `${day}: ${t("leadershipReport.live.status.raised")}`)
          }
        />
        <div className="text-fg-muted mt-1 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px]">
          {legend.map((key) => (
            <span key={key} className="inline-flex items-center gap-1.5">
              <i
                className="inline-block h-[11px] w-[11px] rounded-[3px]"
                style={{ background: channelColor(key) }}
              />
              {L.channel(key)}
            </span>
          ))}
          <span>· {t("leadershipReport.live.def.daily")}</span>
        </div>
      </div>
    </Section>
  );
}

// ── The drill-in drawer ─────────────────────────────────────────────────────

const CSV_COLUMNS: { key: keyof InsightsTicket; label: string }[] = [
  { key: "kind", label: "Type" },
  { key: "number", label: "Number" },
  { key: "subject", label: "Subject" },
  { key: "team", label: "Team" },
  { key: "status", label: "Status" },
  { key: "age_days", label: "Age (days)" },
  { key: "channel", label: "Channel" },
  { key: "sla_response", label: "Response SLA" },
  { key: "sla_resolution", label: "Resolution SLA" },
  { key: "priority", label: "Priority" },
  { key: "offering", label: "Offering" },
  { key: "service", label: "Service" },
  { key: "created", label: "Created (UTC)" },
  { key: "resolved", label: "Resolved (UTC)" },
  { key: "path", label: "Team moves" },
  { key: "site_label", label: "Site" },
  { key: "country", label: "Country" },
];

function TicketDrawer({
  state,
  drill,
  onClose,
}: {
  state: InsightsState;
  drill: Drill | null;
  onClose: () => void;
}) {
  const { t } = useT("common");
  return (
    <Drawer.Root
      side="right"
      open={drill !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <Drawer.Portal>
        <Drawer.Backdrop />
        <Drawer.Viewport>
          <Drawer.Popup size="lg" className="w-full max-w-6xl">
            <Drawer.Header>
              <Drawer.Title>{drill?.title ?? ""}</Drawer.Title>
              <Drawer.CloseButton label={t("button.close")} />
            </Drawer.Header>
            <Drawer.Body className="flex flex-col gap-3 overflow-y-auto">
              {drill ? (
                <DrawerTickets
                  key={`${drill.measure}|${drill.value ?? ""}`}
                  state={state}
                  drill={drill}
                />
              ) : null}
            </Drawer.Body>
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

/**
 * The drawer's list: what each ticket is about (its subject, 28 Oct), a
 * search box and quick filters (type, status, team) that narrow the rows as
 * you type, "x of y" while filtered, and a CSV of exactly the rows shown.
 */
function DrawerTickets({ state, drill }: { state: InsightsState; drill: Drill }) {
  const { t } = useT("common");
  const L = useLabels();
  const tickets = useInsightTickets(HELPDESK_OVERVIEW, state, drill);
  const [sort, setSort] = useState<DataGridSort | null>({
    columnId: "age_days",
    direction: "desc",
  });
  const [filter, setFilter] = useState<TicketFilter>(NO_TICKET_FILTER);
  const items = useMemo(() => tickets.data?.items ?? [], [tickets.data]);
  const shown = useMemo(
    () =>
      filterTickets(items, filter, (row) => [
        L.kind(row.kind),
        L.status(row.status_group),
        L.channel(row.channel_group),
        ticketLabel(row.kind, row.number),
      ]),
    [items, filter, L],
  );
  const sorted = useMemo(
    () => (sort ? sortRows(shown, sort.columnId as keyof InsightsTicket, sort.direction) : shown),
    [shown, sort],
  );
  const choices = useMemo(
    () => ({
      kinds: distinct(items, (row) => row.kind),
      groups: distinct(items, (row) => row.status_group),
      teams: distinct(items, (row) => row.team),
    }),
    [items],
  );
  const filtered = hasTicketFilter(filter);
  const withSubjects = items.some((row) => row.subject);

  const columns = useMemo<DataGridColumn<InsightsTicket>[]>(
    () => [
      {
        id: "number",
        header: t("leadershipReport.live.col.ticket"),
        width: 110,
        sortable: true,
        accessor: (row) => row.number ?? null,
        cell: (row) => {
          const href = ticketHref(row.ticket_id);
          const label = ticketLabel(row.kind, row.number);
          return href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-fg-subtle font-medium hover:underline"
            >
              {label}
            </a>
          ) : (
            label
          );
        },
      },
      {
        id: "subject",
        header: t("leadershipReport.live.col.subject"),
        width: 280,
        sortable: true,
        accessor: (row) => row.subject ?? "",
        cell: (row) =>
          row.subject ? (
            <span className="line-clamp-2" title={row.subject}>
              {row.subject}
            </span>
          ) : (
            <span className="text-fg-muted">–</span>
          ),
      },
      {
        id: "kind",
        header: t("leadershipReport.live.col.type"),
        width: 100,
        sortable: true,
        accessor: (row) => row.kind,
        cell: (row) => L.kind(row.kind),
      },
      {
        id: "team",
        header: t("leadershipReport.live.col.team"),
        sortable: true,
        accessor: (row) => row.team,
      },
      {
        id: "status",
        header: t("leadershipReport.live.col.status"),
        sortable: true,
        accessor: (row) => row.status,
      },
      {
        id: "age_days",
        header: t("leadershipReport.live.col.age"),
        width: 90,
        numeric: true,
        sortable: true,
        accessor: (row) => row.age_days ?? null,
        cell: (row) =>
          row.age_days === null || row.age_days === undefined
            ? "–"
            : t("leadershipReport.live.days", { value: round1(row.age_days) }),
      },
      {
        id: "channel",
        header: t("leadershipReport.live.col.channel"),
        sortable: true,
        accessor: (row) => row.channel,
      },
      {
        id: "sla_response",
        header: t("leadershipReport.live.col.slaResponse"),
        width: 120,
        sortable: true,
        accessor: (row) => row.sla_response,
        cell: (row) => <SlaBadge state={row.sla_response} label={L.sla(row.sla_response)} />,
      },
      {
        id: "sla_resolution",
        header: t("leadershipReport.live.col.slaResolution"),
        width: 120,
        sortable: true,
        accessor: (row) => row.sla_resolution,
        cell: (row) => <SlaBadge state={row.sla_resolution} label={L.sla(row.sla_resolution)} />,
      },
      {
        id: "site",
        header: t("leadershipReport.live.col.site"),
        sortable: true,
        accessor: (row) => row.site_label ?? "",
      },
      {
        id: "moves",
        header: t("leadershipReport.live.col.moves"),
        accessor: (row) => movesLine(row.moves ?? []),
        cell: (row) => {
          const moves = row.moves ?? [];
          if (moves.length === 0) {
            return "–";
          }
          const detail = moves
            .map((m) => `${m.from_team} → ${m.to_team}${m.by ? ` (${m.by})` : ""}`)
            .join("\n");
          return <span title={detail}>{movesLine(moves)}</span>;
        },
      },
    ],
    [t, L],
  );

  function csv() {
    const text = toCsv(sorted, CSV_COLUMNS);
    saveBlob(
      new Blob([text], { type: "text/csv;charset=utf-8" }),
      `helpdesk-tickets-${drill.measure.replace(/_/g, "-")}${filtered ? "-filtered" : ""}.csv`,
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-60">
          <Input
            size="sm"
            value={filter.query}
            onChange={(event) => setFilter({ ...filter, query: event.target.value })}
            placeholder={t("leadershipReport.live.drawer.search")}
            aria-label={t("leadershipReport.live.drawer.search")}
            startSlot={<Icon icon={Search} size="sm" />}
          />
        </div>
        {choices.kinds.length > 1 ? (
          <FilterSelect
            size="sm"
            options={choices.kinds.map((value) => ({ value, label: L.kind(value) }))}
            value={[...filter.kinds]}
            onValueChange={(kinds) => setFilter({ ...filter, kinds })}
            placeholder={t("leadershipReport.live.col.type")}
            noun={t("leadershipReport.live.drawer.typeNoun")}
            searchPlaceholder={t("leadershipReport.live.filter.search")}
            aria-label={t("leadershipReport.live.col.type")}
          />
        ) : null}
        {choices.groups.length > 1 ? (
          <FilterSelect
            size="sm"
            options={choices.groups.map((value) => ({ value, label: L.status(value) }))}
            value={[...filter.groups]}
            onValueChange={(groups) => setFilter({ ...filter, groups })}
            placeholder={t("leadershipReport.live.col.status")}
            noun={t("leadershipReport.live.drawer.statusNoun")}
            searchPlaceholder={t("leadershipReport.live.filter.search")}
            aria-label={t("leadershipReport.live.col.status")}
          />
        ) : null}
        {choices.teams.length > 1 ? (
          <FilterSelect
            size="sm"
            options={choices.teams.map((value) => ({ value, label: value }))}
            value={[...filter.teams]}
            onValueChange={(teams) => setFilter({ ...filter, teams })}
            placeholder={t("leadershipReport.live.col.team")}
            noun={t("leadershipReport.live.filter.teamNoun")}
            searchPlaceholder={t("leadershipReport.live.filter.search")}
            aria-label={t("leadershipReport.live.col.team")}
          />
        ) : null}
        {filtered ? (
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            startIcon={<Icon icon={X} size="sm" />}
            onClick={() => setFilter(NO_TICKET_FILTER)}
          >
            {t("leadershipReport.live.filter.clear")}
          </Button>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={filtered ? "accent" : "neutral"} variant="subtle" aria-live="polite">
          {filtered
            ? t("leadershipReport.live.drawer.countOf", {
                shown: shown.length,
                count: items.length,
              })
            : t("leadershipReport.live.ticketCount", { count: items.length })}
        </Badge>
        <Button
          size="sm"
          variant="outline"
          tone="neutral"
          startIcon={<Icon icon={Download} size="sm" />}
          disabled={shown.length === 0}
          onClick={csv}
        >
          {t("leadershipReport.live.csv")}
        </Button>
        <Typography variant="caption" tone="muted">
          {t("leadershipReport.live.drawerHint")}
          {tickets.data && items.length > 0 && !withSubjects
            ? ` ${t("leadershipReport.live.drawer.noSubjects")}`
            : ""}
        </Typography>
      </div>
      {tickets.isLoading ? (
        <LoadingRows rows={6} />
      ) : tickets.error != null ? (
        <ErrorState error={tickets.error} onRetry={() => void tickets.refetch()} />
      ) : items.length === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {t("leadershipReport.live.nothing")}
        </Typography>
      ) : shown.length === 0 ? (
        <Typography variant="body-sm" tone="muted">
          {t("leadershipReport.live.drawer.noMatch")}
        </Typography>
      ) : (
        <Card padding="none">
          <DataGrid
            aria-label={drill.title}
            columns={columns}
            data={sorted}
            getRowId={(row) => row.ticket_id ?? `${row.kind}-${row.number}`}
            sort={sort}
            onSortChange={setSort}
            density="compact"
          />
        </Card>
      )}
    </>
  );
}

function SlaBadge({ state, label }: { state: string; label: string }) {
  const tone =
    state === "met" ? "success" : state === "missed" || state === "breached" ? "danger" : "neutral";
  return (
    <Badge tone={tone} variant="subtle" size="sm">
      {label}
    </Badge>
  );
}
